import '../css/CategoryPage.css';
import { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import ProductItem from "../components/ProductItem";
import SimpleAlert from '../components/SimpleAlert';
import SessionAlert from '../components/SessionAlert'
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import google_menu from '../assets/google_menu.svg'
import MobileCategoriesBar from '../components/MobileCategoriesCar';
import DesktopCategoriesBar from '../components/DesktopCategoriesBar';
import { fetchJson, responseArray } from '../utils/apiFetch';

export default function CategoryPage() {
    const { category } = useParams();
    const categoryLabels = {
        pain_killers: 'Pain Killers',
        vitamins: 'Vitamins',
        colds: 'Cold/Cough',
        body_care: 'Body Care',
        hygiene: 'Hygiene',
        first_aid: 'First Aid'
    };
    const categoryTitle = categoryLabels[category] || category;
    const [data, setData] = useState([]);
    const [maxPrice, setMaxPrice] = useState(0);
    const [totalProducts, setTotalProducts] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [language, setLanguage] = useState('english');
    const [options, setOptions] = useState([]);
    const [error, setError] = useState(null);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [priceRange, setPriceRange] = useState([0, maxPrice]); // Initial price range
    const [isCollapsed, setIsCollapsed] = useState(true);
    const productsPerPage = 12;

    useEffect(() => {
        setCurrentPage(1);
        fetchProductsByPage(1, true);
    }, [category]);

    useEffect(() => {
        if (currentPage !== 1) {
            fetchProductsByPage(currentPage);
        }
    }, [currentPage]);

    const fetchProductsByPage = async (page, includeCount = false) => {
        setLoading(true);
        setError(null);
        if(data.length > 0) {
            setData([])
        }
        try {
            const endpoint = `/api/product/category/${category}?page=${page}&limit=${productsPerPage}${includeCount ? '&includeCount=true' : ''}`;
            const response = await fetchJson(endpoint);
            const products = responseArray(response, 'products');
            const count = Number(response?.count ?? products.length);
            const uniqueBrands = [...new Set(products.map(product => product.brand))];
            const newOptions = uniqueBrands.map(brand => ({
                label: brand,
                value: brand
            }));
            const nextMaxPrice = products.length > 0 ? Math.max(...products.map(product => Number(product.price || 0))) : 0;
            setOptions(newOptions);
            setData(products);
            setMaxPrice(nextMaxPrice);
            if (includeCount) {
                setTotalProducts(count);
                setTotalPages(Math.ceil(count / productsPerPage));
            }
            setPriceRange(prev => {
                if (includeCount) {
                    return [0, nextMaxPrice];
                }

                const minValue = Math.min(prev[0], nextMaxPrice);
                const maxValue = Math.min(prev[1], nextMaxPrice);
                return [minValue, maxValue];
            });
        } catch (error) {
            console.error('Error:', error);
            setError('Failed to fetch products.');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleCheckboxChange = (event) => {
        const { value } = event.target;
        setSelectedBrands(prev =>
            prev.includes(value) ? prev.filter(brand => brand !== value) : [...prev, value]
        );
    };

    const handlePriceChange = (event, newValue) => {
        setPriceRange(newValue); // Update the selected price range
    };

    const filteredData = data.filter(product =>
        (selectedBrands.length === 0 || selectedBrands.includes(product.brand)) &&
        product.price >= priceRange[0] && product.price <= priceRange[1]
    );    

    const hasAnyProductsInCategory = totalProducts > 0;

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <div dir={language === 'arabic' ? 'rtl' : 'ltr'} className='categorypage-container' style={{marginLeft: isCollapsed ? '' : '0', marginRight: isCollapsed ? '' : '0'}}>
            <MobileCategoriesBar {...{ language, isCollapsed, toggleSidebar, priceRange, handlePriceChange, maxPrice, options, selectedBrands, loading, handleCheckboxChange }}/>
            <SessionAlert />
            <div className='mobile-categorymenu' style={{ opacity: isCollapsed ? 1 : 0.5}}>
                <button onClick={toggleSidebar}>
                    <img src={google_menu}></img>
                </button>
                <h2 id='category-h2'>{categoryTitle}</h2>
            </div>

            {/* FOR DESKTOP */}
            <section className={`big-category-container ${isCollapsed ? '' : 'bigcontainer-opacity'}`} style={{ opacity: isCollapsed ? 1 : 0.5}}>
                <SimpleAlert id="addcart-alert" key="alert" message="Added to cart" type='success' />
                <DesktopCategoriesBar {...{language, isCollapsed, priceRange, handlePriceChange, maxPrice, options, selectedBrands, loading, handleCheckboxChange }}/>
                <aside id="productsAside">
                    <div className="category-container">
                        {Array.from({ length: loading ? productsPerPage : 0 }).map((_, index) => (
                            <Box key={index} sx={{ pt: 0.5, display: loading ? 'block' : 'none' }}>
                                <Skeleton variant="rectangular" width={210} height={118} />
                                <Skeleton />
                                <Skeleton width="60%" />
                            </Box>
                        ))}
                        {error ? (
                            <div>Error: {error}</div>
                        ) : !loading && !hasAnyProductsInCategory ? (
                            <div style={{ padding: '1rem', color: '#666' }}>
                                No products found in this category.
                            </div>
                        ) : !loading && filteredData.length === 0 ? (
                            <div style={{ padding: '1rem', color: '#666' }}>
                                No products match the selected filters.
                            </div>
                        ) : (
                            filteredData.map(product => (
                                <ProductItem key={product._id} product={product} nb_in_stock={product.nb_in_stock} language={language}/>
                            ))
                        )}
                    </div>
                    {totalPages > 1 && (
                        <div id='category-pagination'>
                            <Stack spacing={2}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    variant="outlined"
                                    color="primary"
                                />
                            </Stack>
                        </div>
                    )}
                </aside>
            </section>
        </div>
    );
}
